<template>
  <div id="deal_conversion_graph">
    <ContainerInfoItem
      source="deal_conversion_graph"
      :title="title"
      :description="range.label"
      height="fit-content"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <LineChart
        v-else-if="chartCategories && chartData"
        id="call_engagement_activity"
        primary-color="#A146E8"
        :categories="chartCategories"
        :series-data="chartData"
      />

      <EmptyScreen
        v-else
        source="call_engagement_activity"
        text="No data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watchEffect } from "vue";
import store from "@/src/store/index.js";
import LineChart from "@/src/components/charts/LineChart.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import AnalyticService from "@/src/api/AnalyticService.js";
import { processLineChartData } from "@/src/utils/chartUtils.js";
import { dealConversionTypes } from "@/src/constants/analyticConstants.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  title: { type: String, required: true },
  type: {
    type: String,
    required: true,
    validator: (value) =>
      [dealConversionTypes.COUNT, dealConversionTypes.AMOUNT].includes(value),
  },
});
const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const processDealConversionData = (data) => {
  return processLineChartData(data, {
    dateKey: "date",
    metrics: [{ key: "conversionRate", label: "Conversion Rate" }],
  });
};

const toFetchDealConversionValue = async () => {
  try {
    isLoading.value = true;

    const params = {
      deal_owner_id: userIds.value,
      team_id: teamIds.value,
      pipeline_id: filters.value?.pipeline?.id,
      start_time: range.value?.start,
      end_time: range.value?.end,
      type: props.type,
    };

    const response = await AnalyticService.V2.fetchDealConversionValue(params);
    if (
      response?.success &&
      response?.data &&
      Object.keys(response?.data).length > 0
    ) {
      const { categories, seriesData } = processDealConversionData(
        response.data,
      );
      chartCategories.value = categories;
      chartData.value = seriesData;
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching deal_conversion result: ",
      error,
    );
  } finally {
    isLoading.value = false;
  }
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value)
    toFetchDealConversionValue();
});

useAnalyticsRefresh(toFetchDealConversionValue, [range, userIds, teamIds]);
</script>
